import { useToast } from '@helix-ds/react'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { track } from '../../../utils/analytics'
import { withSupportMessage } from '../../../utils/errorMessage'
import { useFamilyPortalApiClient } from '../../auth/hooks/useFamilyPortalApiClient'
import { buildSecondaryDocumentContent, resolveSecondarySectionTitle } from './consentOptionsUtils'
import type { ConsentConfigItem, ConsentConfigReference } from './types'

export function useSecondaryConsent(consentConfig: ConsentConfigReference | null, locale: string) {
  const apiClient = useFamilyPortalApiClient()
  const intl = useIntl()
  const { addNotification } = useToast()
  const [toggles, setToggles] = useState<Record<string, boolean>>({})
  const [initialToggles, setInitialToggles] = useState<Record<string, boolean>>({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!consentConfig) {
      setToggles({})
      setInitialToggles({})
      return
    }

    setToggles({})
    setInitialToggles({})

    const params = new URLSearchParams({
      configId: consentConfig.configId,
      configVersionId: consentConfig.configVersionId,
      consentType: 'secondary',
    })
    let cancelled = false
    apiClient
      .get(`/consents/secondary?${params.toString()}`)
      .then((res) => {
        if (cancelled || !res.ok) return
        return res.json()
      })
      .then((data: { itemStates?: Record<string, boolean> } | undefined) => {
        if (cancelled) return
        const itemStates = data?.itemStates ?? {}
        setToggles(itemStates)
        setInitialToggles(itemStates)
      })
      .catch(() => {
        // Existing choices are optional context; leave the flow usable if loading them fails.
      })
    return () => {
      cancelled = true
    }
  }, [apiClient, consentConfig, locale])

  async function saveChoice(item: ConsentConfigItem, sectionTitle: string, value: boolean) {
    if (!consentConfig) return false
    setSaving(true)
    try {
      const res = await apiClient.post('/consents/secondary', {
        configId: consentConfig.configId,
        configVersionId: consentConfig.configVersionId,
        consentType: 'secondary',
        entries: [{ itemId: item.id, sectionTitle: resolveSecondarySectionTitle(sectionTitle), isConsented: value }],
        documentContent: buildSecondaryDocumentContent(item, locale),
      })
      if (res.ok) {
        const isUpdate = initialToggles[item.id] !== undefined
        track('consent.secondary_decision', { item_id: item.id, accepted: value, is_update: isUpdate })
        setInitialToggles((prev) => ({ ...prev, [item.id]: value }))
        return true
      }
      const data = await res.json().catch(() => ({}))
      const message =
        typeof data?.detail === 'string'
          ? data.detail
          : res.status === 404
            ? intl.formatMessage({
                id: 'consentOptions.error.noConsentContext',
                defaultMessage: 'No consent context found. Please refresh and try again.',
              })
            : intl.formatMessage({
                id: 'consentOptions.error.couldNotSavePreference',
                defaultMessage: "We couldn't save your preference. Please try again.",
              })
      track('consent.submission_failed', { type: 'secondary', item_id: item.id, status: res.status })
      addNotification({
        type: 'error',
        title: intl.formatMessage({
          id: 'consentOptions.toast.couldNotSavePreferenceTitle',
          defaultMessage: "Couldn't save preference",
        }),
        message: withSupportMessage(intl, message),
      })
      return false
    } catch {
      track('consent.submission_failed', { type: 'secondary', item_id: item.id, status: 'network_error' })
      addNotification({
        type: 'error',
        title: intl.formatMessage({
          id: 'consentOptions.toast.couldNotSavePreferenceTitle',
          defaultMessage: "Couldn't save preference",
        }),
        message: withSupportMessage(
          intl,
          intl.formatMessage({
            id: 'consentOptions.error.couldNotSavePreference',
            defaultMessage: "We couldn't save your preference. Please try again.",
          })
        ),
      })
      return false
    } finally {
      setSaving(false)
    }
  }

  async function saveDualChoices(
    item: ConsentConfigItem,
    sectionTitle: string,
    valueSelf: boolean,
    valueDependent: boolean
  ) {
    if (!consentConfig) return false
    setSaving(true)
    try {
      const res = await apiClient.post('/consents/secondary', {
        configId: consentConfig.configId,
        configVersionId: consentConfig.configVersionId,
        consentType: 'secondary',
        entries: [
          {
            itemId: item.id,
            sectionTitle: resolveSecondarySectionTitle(sectionTitle),
            isConsented: valueSelf,
            subjectScope: 'SELF',
          },
          {
            itemId: item.id,
            sectionTitle: resolveSecondarySectionTitle(sectionTitle),
            isConsented: valueDependent,
            subjectScope: 'DEPENDENT',
          },
        ],
        documentContent: buildSecondaryDocumentContent(item, locale),
      })
      if (res.ok) {
        const keySelf = `${item.id}:SELF`
        const keyDep = `${item.id}:DEPENDENT`
        const isUpdate = initialToggles[keySelf] !== undefined || initialToggles[keyDep] !== undefined
        track('consent.secondary_decision', {
          item_id: item.id,
          accepted_self: valueSelf,
          accepted_dependent: valueDependent,
          is_dual: true,
          is_update: isUpdate,
        })
        setInitialToggles((prev) => ({ ...prev, [keySelf]: valueSelf, [keyDep]: valueDependent }))
        return true
      }
      const data = await res.json().catch(() => ({}))
      const message =
        typeof data?.detail === 'string'
          ? data.detail
          : res.status === 404
            ? intl.formatMessage({
                id: 'consentOptions.error.noConsentContext',
                defaultMessage: 'No consent context found. Please refresh and try again.',
              })
            : intl.formatMessage({
                id: 'consentOptions.error.couldNotSavePreference',
                defaultMessage: "We couldn't save your preference. Please try again.",
              })
      track('consent.submission_failed', { type: 'secondary_dual', item_id: item.id, status: res.status })
      addNotification({
        type: 'error',
        title: intl.formatMessage({
          id: 'consentOptions.toast.couldNotSavePreferenceTitle',
          defaultMessage: "Couldn't save preference",
        }),
        message: withSupportMessage(intl, message),
      })
      return false
    } catch {
      track('consent.submission_failed', { type: 'secondary_dual', item_id: item.id, status: 'network_error' })
      addNotification({
        type: 'error',
        title: intl.formatMessage({
          id: 'consentOptions.toast.couldNotSavePreferenceTitle',
          defaultMessage: "Couldn't save preference",
        }),
        message: withSupportMessage(
          intl,
          intl.formatMessage({
            id: 'consentOptions.error.couldNotSavePreference',
            defaultMessage: "We couldn't save your preference. Please try again.",
          })
        ),
      })
      return false
    } finally {
      setSaving(false)
    }
  }

  return {
    toggles,
    setToggles,
    saving,
    saveChoice,
    saveDualChoices,
  }
}
